import {
  Log,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from "oidc-client-ts";

import { getCookie, setCookie } from "@/helpers/cookies";

declare module "oidc-client-ts" {
  export interface UserManager {
    signinRedirect(): Promise<never>;
  }
}

import { i18n } from "@/locales/index";
import { ApiClient } from "@/api-client";
const apiClient = ApiClient.getInstance();

export class LoginClient {
  private static instance: LoginClient;
  //client
  private oidcClient: UserManager;

  private constructor() {
    if (!import.meta.env.PROD) {
      Log.setLogger(console);
      Log.setLevel(Log.INFO);
    }
    this.oidcClient = new UserManager(LoginClient.clientConfig);

    //#region events
    //access token exp
    this.oidcClient.events.addAccessTokenExpired(() => {
      console.warn("addAccessTokenExpired");
      window.sessionStorage.clear();
      window.location.reload();
    });

    //call subscribed listeners on event
    this.oidcClient.events.addSilentRenewError(async (error) => {
      try {
        console.log("Silent signin");
        await this.oidcClient.signinSilent();
      } catch (error) {
        console.error("silent token renew failed");
        for (const listener of this.silentRenewErrorListeners) {
          listener(error instanceof Error ? error : new Error(String(error)));
        }
      }
    });

    //set token on loaded user
    this.oidcClient.events.addUserLoaded(async () => {
      apiClient.setBearerToken((await this.getUser()).access_token);
    });

    //#endregion

    //get and parse marker for login process start
    const marker = sessionStorage.getItem("login_triggered");
    let parsedMarker;
    try {
      parsedMarker = JSON.parse(String(marker));
    } catch (error) {
      parsedMarker = false;
    }

    //first time loading, no session, set marker (to avoid a signinRedirect-loop after returning from idp) and redirect
    if (!parsedMarker) {
      sessionStorage.setItem("login_triggered", "true");
      try {
        this.oidcClient.signinRedirect();
      } catch (error) {
        console.error("Signin redirect failed");
        throw new Error(i18n.global.t("app.user.loadingData.error"));
      }
    }
  }

  //event listeners
  private silentRenewErrorListeners: ((error: Error) => void)[] = [];
  public onSilentRenewError(listener: (error: Error) => void) {
    this.silentRenewErrorListeners.push(listener);
  }

  //oid config
  private static readonly clientConfig: UserManagerSettings = {
    authority: import.meta.env.VITE_OIDC_PROVIDER,
    client_id: import.meta.env.VITE_CLIENT_ID,
    client_secret: import.meta.env.VITE_CLIENT_SECRET,
    redirect_uri: import.meta.env.VITE_REDIRECT_URL,
    response_type: "code",
    scope: "openid",
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    post_logout_redirect_uri: import.meta.env.VITE_REDIRECT_URL,
    automaticSilentRenew: true,
  };

  static getInstance(): LoginClient {
    //check if no instance
    if (!LoginClient.instance) LoginClient.instance = new LoginClient();
    return LoginClient.instance;
  }

  logOut(): void {
    this.oidcClient.signoutRedirect();
  }

  async getUser(): Promise<User> {
    let user: User | null = null;

    // probably the page was reloaded so we are already signed in
    try {
      user = await this.oidcClient.getUser();
      if (!user) throw new Error();
    } catch (initialError) {
      //if failed, redirect again
      console.log("Can't get user");
      console.log(initialError);

      try {
        user = await this.oidcClient.signinRedirectCallback();
        window.history.replaceState({}, document.title, "/");
      } catch (error) {
        //failed to process information (maybe expired or invalid), redirect again
        try {
          await this.oidcClient.signinRedirect();
        } catch (error) {
          //try again after delay once every 5 minutes
          if (!getCookie("signinRedirectFailed")) {
            setCookie("signinRedirectFailed", "true", 0.003472);
            setTimeout(() => {
              window.stop();
              this.oidcClient.signinRedirect();
            }, 2000);
          }

          throw new Error(
            `${
              error instanceof Error ? error.message : String(error)
            }. Maybe the Identity provider is offline.`
          );
        }
      }
    }

    if (!user) throw new Error(i18n.global.t("app.user.loadingData.error"));

    return user;
  }
}
